import { useCallback, useRef, useMemo, useEffect } from 'react';
import { AppNode, NodeType, NodeStatus, ImageGenerationMode } from '@/types';
import { useNodeStore } from '@/stores/nodeStore';

export interface UseGenerateNodeActionsOptions {
  data: AppNode;
  localPrompt: string;
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onAddNode?: (node: AppNode, position: { x: number; y: number }, options?: { connectToNodeId?: string }) => void;
  onSetLocalPrompt: (value: string) => void;
}

export function useGenerateNodeActions(options: UseGenerateNodeActionsOptions) {
  const { data, localPrompt, onNodeAction, onNodeUpdate, onAddNode, onSetLocalPrompt } = options;
  const onNodeUpdateRef = useRef(onNodeUpdate);
  onNodeUpdateRef.current = onNodeUpdate;

  const commitPrompt = useCallback(() => {
    if (localPrompt !== (data.data.prompt || '')) {
      onNodeUpdateRef.current(data.id, { prompt: localPrompt });
    }
  }, [data.id, data.data.prompt, localPrompt]);

  const handleGenerate = useCallback(() => {
    commitPrompt();
    onNodeAction(data.id, localPrompt);
  }, [data.id, localPrompt, commitPrompt, onNodeAction]);

  const handlePromptChange = useCallback((value: string) => {
    onSetLocalPrompt(value);
  }, [onSetLocalPrompt]);

  const handlePromptSubmit = useCallback(() => {
    commitPrompt();
  }, [commitPrompt]);

  const handleImageModeSwitch = useCallback((targetMode: ImageGenerationMode) => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return;

    const needsInput = targetMode !== 'TXT2IMG';
    const hasInput = data.inputs.length > 0;

    if (needsInput && !hasInput && onAddNode) {
      const newNodeId = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const uploadPosition = { x: data.x - 480, y: data.y };
      const modeLabel = targetMode === 'IMG2IMG' ? '图生图' : targetMode === 'UPSCALE' ? '图片高清' : '风格迁移';

      const newNode: AppNode = {
        id: newNodeId,
        type: NodeType.MEDIA_UPLOAD,
        x: uploadPosition.x,
        y: uploadPosition.y,
        width: 420,
        title: modeLabel,
        status: NodeStatus.IDLE,
        data: {},
        inputs: [],
      };

      onAddNode(newNode, uploadPosition, { connectToNodeId: data.id });
    }

    onNodeUpdateRef.current(data.id, { generationMode: targetMode });
  }, [data.type, data.id, data.x, data.y, data.inputs.length, onAddNode]);

  const handleAddUploadNodeForPanorama = useCallback(() => {
    if (data.type !== NodeType.PANORAMA_GENERATOR || !onAddNode) return;

    const newNodeId = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const uploadPosition = { x: data.x - 480, y: data.y };

    const newNode: AppNode = {
      id: newNodeId,
      type: NodeType.MEDIA_UPLOAD,
      x: uploadPosition.x,
      y: uploadPosition.y,
      width: 420,
      title: '图生全景',
      status: NodeStatus.IDLE,
      data: {},
      inputs: [],
    };

    onAddNode(newNode, uploadPosition, { connectToNodeId: data.id });
    onNodeUpdateRef.current(data.id, { panoramaMode: 'img2panorama' });
  }, [data.type, data.id, data.x, data.y, onAddNode]);

  const handleTryAction = useCallback((actionId: string) => {
    if (data.type === NodeType.IMAGE_GENERATOR) {
      const modeMap: Record<string, ImageGenerationMode> = {
        'img2img': 'IMG2IMG',
        'upscale': 'UPSCALE',
      };
      const targetMode = modeMap[actionId];
      if (targetMode) {
        handleImageModeSwitch(targetMode);
      }
    }

    if (data.type === NodeType.PROMPT_INPUT) {
      if (actionId === 'write_content') {
        // 切换到自己编写内容模式，保持原节点尺寸
        const nextIndex = useNodeStore.getState().getNextTextNodeIndex();
        onNodeUpdateRef.current(data.id, {
          subMode: 'self-write',
          editMode: 'preview',
          textNodeIndex: nextIndex,
          nodeSize: { width: data.width || 420, height: data.height || 236 },
        });
      }
      // 其他子模式（txt2video, img2prompt, txt2music）后续扩展
    }
  }, [data.type, data.id, handleImageModeSwitch]);

  const currentImageMode = useMemo((): ImageGenerationMode => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return 'TXT2IMG';
    const mode = data.data.generationMode as ImageGenerationMode | undefined;
    const hasInput = data.inputs.length > 0;
    if (mode && ['TXT2IMG', 'IMG2IMG', 'UPSCALE', 'STYLE'].includes(mode)) {
      if (mode !== 'TXT2IMG' && !hasInput) return 'TXT2IMG';
      return mode;
    }
    return hasInput ? 'IMG2IMG' : 'TXT2IMG';
  }, [data.type, data.data.generationMode, data.inputs.length]);

  // 当生成模式需要输入但节点没有输入时，自动切回 TXT2IMG 模式
  useEffect(() => {
    if (data.type !== NodeType.IMAGE_GENERATOR) return;
    const mode = data.data.generationMode as ImageGenerationMode | undefined;
    if (!mode || mode === 'TXT2IMG') return;
    if (data.inputs.length === 0) {
      onNodeUpdateRef.current(data.id, { generationMode: 'TXT2IMG' });
    }
  }, [data.type, data.data.generationMode, data.inputs.length, data.id]);

  // 当视频模式没有输入时，设置为 txt2video；有输入时设置为 omni
  useEffect(() => {
    if (data.type !== NodeType.VIDEO_GENERATOR) return;
    const currentMode = data.data.generationMode as string | undefined;
    if (data.inputs.length > 0 && currentMode === 'txt2video') {
      onNodeUpdateRef.current(data.id, { generationMode: 'omni' });
    } else if (data.inputs.length === 0 && currentMode && currentMode !== 'txt2video') {
      onNodeUpdateRef.current(data.id, { generationMode: 'txt2video' });
    }
  }, [data.type, data.inputs.length, data.id]);

  return {
    onNodeUpdateRef,
    commitPrompt,
    handleGenerate,
    handlePromptChange,
    handlePromptSubmit,
    handleImageModeSwitch,
    handleAddUploadNodeForPanorama,
    handleTryAction,
    currentImageMode,
  };
}
